/* Email Popup - TT
 *
 * Opens once per visitor per N days, walks three steps, and posts the address
 * to Shopify's own customer form so the signup lands where every other signup
 * on the store lands.
 *
 * Order: turn three cards, then answer one question, then leave an address.
 *
 * The card turn is a reveal, not a lottery: every card carries the same reward,
 * so "match three" is always true. Nothing here decides a prize.
 */
(function () {
  'use strict';

  var SEEN = 'seen';
  var DONE = 'done';

  function readState(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private browsing — the popup simply shows again next visit */
    }
  }

  class TtEmailPopup extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.key = this.dataset.key || 'tt-pop';
      this.design = !!(window.Shopify && window.Shopify.designMode);
      this.steps = {};

      this.querySelectorAll('[data-tt-pop-step]').forEach(
        function (el) {
          this.steps[el.dataset.ttPopStep] = el;
        }.bind(this)
      );

      this.bind();
      this.ensureKlaviyo();

      if (this.design) {
        // In the theme editor it should just be visible, every time.
        this.open();
        return;
      }

      if (this.suppressed()) return;
      this.arm();
    }

    suppressed() {
      var state = readState(this.key);
      if (!state) return false;
      if (state.status === DONE) return true;

      var days = parseInt(this.dataset.frequency, 10) || 30;
      return Date.now() - (state.at || 0) < days * 864e5;
    }

    arm() {
      var open = this.open.bind(this);
      var delay = (parseFloat(this.dataset.delay) || 0) * 1000;

      this.timer = window.setTimeout(open, delay);

      if (this.dataset.exitIntent === 'true' && window.matchMedia('(min-width: 750px)').matches) {
        this.onLeave = function (e) {
          if (e.clientY <= 0) open();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    disarm() {
      window.clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }

    bind() {
      var self = this;

      this.querySelectorAll('[data-tt-pop-close]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.close();
        });
      });

      this.onKey = function (e) {
        if (e.key === 'Escape') self.close();
      };

      this.querySelectorAll('[data-tt-pop-choice]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.answer = el.dataset.ttPopChoice || '';
          self.track('Popup Answered', { source: 'Welcome popup', signup_reason: self.answer });
          self.go('email');
        });
      });

      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-tt-pop-card]'));
      this.cards.forEach(function (card) {
        card.addEventListener('click', function () {
          self.turn(card);
        });
      });

      this.form = this.querySelector('form');
      if (this.form) {
        // Never let the browser submit this form: the theme picks native
        // submits up and navigates away mid-flow.
        this.form.addEventListener('submit', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.send();
        });
        this.form.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            self.send();
          }
        });
      }

      var send = this.querySelector('[data-tt-pop-send]');
      if (send) send.addEventListener('click', this.send.bind(this));

      var sms = this.querySelector('[data-tt-pop-send-sms]');
      if (sms) sms.addEventListener('click', this.sendSms.bind(this));

      var skip = this.querySelector('[data-tt-pop-skip-sms]');
      if (skip) {
        skip.addEventListener('click', function () {
          self.track('Popup SMS Skipped', { source: 'Welcome popup' });
          self.go('done');
        });
      }

      var phone = this.querySelector('[data-tt-pop-phone]');
      if (phone) {
        phone.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            self.sendSms();
          }
        });
      }
    }

    go(name) {
      this.step = name;
      Object.keys(this.steps).forEach(
        function (key) {
          this.steps[key].hidden = key !== name;
        }.bind(this)
      );

      var focusable = this.steps[name] && this.steps[name].querySelector('input, button, a');
      if (focusable && name === 'email') focusable.focus({ preventScroll: true });
    }

    turn(card) {
      if (card.classList.contains('is-open') || this.settled) return;
      card.classList.add('is-open');
      card.setAttribute('aria-pressed', 'true');

      var turned = this.cards.filter(function (c) {
        return c.classList.contains('is-open');
      });
      if (turned.length < 3) return;

      // Three turned: the match stands and the offer is earned. The rest stay
      // face down, as in the game this is modelled on.
      this.settled = true;
      this.track('Popup Cards Turned', { source: 'Welcome popup' });
      var self = this;
      window.setTimeout(function () {
        self.go('offer');
      }, 1100);
    }

    send() {
      var form = this.form;
      if (!form || this.sending) return;

      var field = form.querySelector('input[type="email"]');
      if (field && !field.checkValidity()) {
        field.reportValidity();
        return;
      }

      var error = this.querySelector('[data-tt-pop-error]');
      var button = this.querySelector('[data-tt-pop-send]');

      var tags = form.querySelector('[data-tt-pop-tags]');
      if (tags && this.answer) tags.value = 'newsletter,' + this.answer;

      if (error) error.hidden = true;
      if (button) button.disabled = true;
      this.sending = true;

      var self = this;
      var email = field ? field.value.trim() : '';

      // Two homes for one address: Shopify keeps the customer record and the
      // tag, Klaviyo gets the profile now so the welcome flow can fire without
      // waiting on the nightly-ish sync. Either one landing is a success.
      Promise.allSettled([
        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' },
        }).then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return 'shopify';
        }),
        this.toKlaviyo(email, null),
      ])
        .then(function (results) {
          var landed = results.some(function (r) {
            return r.status === 'fulfilled' && r.value;
          });
          if (!landed) throw new Error('nowhere');
          self.track('Popup Submitted', {
            source: 'Welcome popup',
            signup_reason: self.answer || '',
            $email: email,
          });
          writeState(self.key, { status: DONE, at: Date.now() });

          var sent = self.querySelector('[data-tt-pop-sent]');
          if (sent) sent.textContent = sent.textContent.replace('[email]', email);

          self.go(self.steps.phone ? 'phone' : 'done');
        })
        .catch(function () {
          if (error) error.hidden = false;
        })
        .finally(function () {
          if (button) button.disabled = false;
          self.sending = false;
        });
    }

    // A view happens before there's an email, so attributing it needs Klaviyo's
    // onsite script to identify the anonymous visitor. The app embed loads it on
    // the live theme; load it ourselves anywhere it is missing, so view counts
    // (and therefore the submit rate) do not depend on that embed being on.
    ensureKlaviyo() {
      var company = this.dataset.klaviyoCompany;
      if (!company) return;

      // klaviyo.js drains this queue on load, and keeps accepting pushes after —
      // so this is the one place to send events, loaded or not. Never assign
      // window.klaviyo: the real script installs a Proxy there.
      window._klOnsite = window._klOnsite || [];

      if (window.klaviyo) return;
      if (document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]')) return;

      var el = document.createElement('script');
      el.async = true;
      el.src =
        'https://static.klaviyo.com/onsite/js/' +
        encodeURIComponent(company) +
        '/klaviyo.js?company_id=' +
        encodeURIComponent(company);
      document.head.appendChild(el);
    }

    // Klaviyo's onsite script knows who an anonymous visitor is, so it can
    // record a view before there's an email. Without it (draft themes, app
    // embed off) only events carrying an email can be sent.
    track(name, props) {
      var data = props || {};
      this.toAnalytics(name, data);

      var company = this.dataset.klaviyoCompany;
      if (!company) return;

      if (window._klOnsite) {
        window._klOnsite.push(['track', name, data]);
        return;
      }

      var email = data.$email;
      if (!email) return;

      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: data,
              metric: { data: { type: 'metric', attributes: { name: name } } },
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
          },
        }),
      }).catch(function () {
        /* measurement must never break the signup */
      });
    }

    // GA4 runs inside Shopify's pixel sandbox (the Google & YouTube app), so
    // there is no gtag on this page to call. Publish a Shopify customer event
    // instead; the "TT popup → GA4" custom pixel (Settings → Customer events)
    // forwards it. GA4 forbids personal data, so no address or number goes.
    toAnalytics(name, data) {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function' || this.design) return;

      var safe = { popup_event: name, popup_source: data.source || 'Welcome popup' };
      if (data.signup_reason) safe.signup_reason = data.signup_reason;
      if (data.step) safe.popup_step = data.step;

      try {
        analytics.publish('tt_popup', safe);
      } catch (e) {
        /* measurement must never break the signup */
      }
    }

    sendSms() {
      var input = this.querySelector('[data-tt-pop-phone]');
      var error = this.querySelector('[data-tt-pop-sms-error]');
      var button = this.querySelector('[data-tt-pop-send-sms]');
      if (!input || this.sendingSms) return;

      // US store: accept what a parent actually types and normalise it
      var digits = (input.value || '').replace(/[^0-9]/g, '');
      if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
      if (digits.length !== 10) {
        if (error) error.hidden = false;
        input.focus();
        return;
      }

      var phone = '+1' + digits;
      if (error) error.hidden = true;
      if (button) button.disabled = true;
      this.sendingSms = true;

      var self = this;
      this.toKlaviyo(null, phone)
        .then(function (res) {
          if (!res) throw new Error('sms');
          self.track('Popup SMS Submitted', { source: 'Welcome popup', $phone_number: phone });
          self.go('done');
        })
        .catch(function () {
          if (error) error.hidden = false;
        })
        .finally(function () {
          if (button) button.disabled = false;
          self.sendingSms = false;
        });
    }

    toKlaviyo(email, phone) {
      var company = this.dataset.klaviyoCompany;
      var list = phone ? this.dataset.klaviyoSmsList : this.dataset.klaviyoList;
      if (!company || !list || (!email && !phone)) return Promise.resolve(null);

      var who = phone ? { phone_number: phone } : { email: email };

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Welcome popup',
            profile: {
              data: {
                type: 'profile',
                attributes: Object.assign({}, who, {
                  properties: {
                    signup_reason: this.answer || '',
                    signup_source: 'Welcome popup',
                  },
                }),
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      };

      // The list relationship is what subscribes them; a `subscriptions` block
      // on the profile is rejected by this endpoint (400).
      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.ok ? 'klaviyo' : null;
        })
        .catch(function () {
          return null;
        });
    }

    open() {
      if (!this.hidden) return;
      this.disarm();
      this.hidden = false;
      document.addEventListener('keydown', this.onKey);
      document.documentElement.style.overflow = 'hidden';
      this.step = Object.keys(this.steps).find(
        function (key) {
          return !this.steps[key].hidden;
        }.bind(this)
      );
      if (!this.design) {
        writeState(this.key, { status: SEEN, at: Date.now() });
        this.track('Popup Viewed', { source: 'Welcome popup', step: this.step });
      }
    }

    close() {
      if (this.hidden) return;
      // which step they walked away from is the whole point of the funnel;
      // GA only — Klaviyo can't record an anonymous visitor anyway
      if (!this.design) this.toAnalytics('Popup Closed', { source: 'Welcome popup', step: this.step });
      this.hidden = true;
      this.disarm();
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.style.overflow = '';
    }
  }

  if (!customElements.get('tt-email-popup')) {
    customElements.define('tt-email-popup', TtEmailPopup);
  }
})();
